/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  challengeSelector,
  challengeResultsSelector,
} from 'reducers/challenge.reducer';
import { userInfosSelector } from 'reducers/app.reducer';
import {
  AccessDenied,
  PhotoContestDescription,
  PhotoContestRank,
  PhotoContestResults,
  Banner,
} from 'components';
import { ChallengeDatesType } from 'interfaces';
import { useImgSrc } from 'core/utils/use-img-src.hook';
import { getChallengePhase } from 'core/utils/challenges.helpers';
import { getFormatedDates } from 'core/utils/date.helpers';
import { CHALLENGE_PHASE } from '../../../../constants';
import {
  Container,
  PageContainer,
} from 'components/ChallengeDetailsContainers';
import { IResult } from 'interfaces/challenge.types';

const DetailsChallengePhoto = ({
  challengeId,
}: {
  challengeId: number;
}): JSX.Element | null => {
  const {
    i18n: { language },
  } = useTranslation();
  const { id: userId } = useSelector(userInfosSelector);
  const { data } = useSelector(challengeSelector(challengeId));
  const { status: resultsLoadingStatus, totalItems } = useSelector(
    challengeResultsSelector(challengeId),
  );

  const {
    endDate,
    endDiffusionDate,
    image: PageBanner,
    startDate,
    startDiffusionDate,
    teaser,
    title,
    type,
  } = data || {};
  const { fullUri } = useImgSrc(PageBanner) || {};
  const currentUserResult: IResult | undefined = data?.challengeResults?.find(
    (result) => result.targetId === userId,
  );

  const dates: ChallengeDatesType = getFormatedDates(
    {
      challenge: {
        start: startDate,
        end: endDate,
      },
      diffusion: {
        start: startDiffusionDate,
        end: endDiffusionDate,
      },
    },
    { day: 'numeric', month: 'short', year: 'numeric' },
    language,
  );

  const currentPhase = getChallengePhase(dates);

  const isChallengeVisible =
    currentPhase !== CHALLENGE_PHASE.BEFORE_DIFFUSION &&
    currentPhase !== CHALLENGE_PHASE.AFTER_DIFFUSION;

  if (!isChallengeVisible) {
    return <AccessDenied />;
  }

  const hasChallengeStarted =
    currentPhase === CHALLENGE_PHASE.DURING_CHALLENGE ||
    currentPhase === CHALLENGE_PHASE.AFTER_CHALLENGE;

  return (
    <Container>
      <Banner
        challengeType={type}
        dates={dates}
        half={false}
        image={fullUri}
        isParticipating
      />

      <PageContainer>
        <PhotoContestDescription teaser={teaser} title={title} />
        <PhotoContestResults
          challengeId={challengeId}
          currentPhase={currentPhase}
          currentUserResult={currentUserResult}
          resultsLoadingStatus={resultsLoadingStatus}
          totalParticipatingUsers={totalItems}
        />
        {hasChallengeStarted && (
          <PhotoContestRank
            challengeId={challengeId}
            currentPhase={currentPhase}
          />
        )}
      </PageContainer>
    </Container>
  );
};

export default DetailsChallengePhoto;
